import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appointmentReminderJobService } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  ChevronLeftIcon,
  SearchIcon,
  FilterIcon,
  RefreshIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PatientsIcon,
  CallLogIcon,
  InfoCircleIcon,
  ArrowPathIcon,
  PencilIcon,
  TrashIcon
} from '../assets/icons';
import { useLanguage } from '../contexts/LanguageContext';
import Pagination from '../components/Pagination';
import SearchBar from '../components/SearchBar';
import WABATemplatePreviewDialog from '../components/WABATemplatePreviewDialog';
import EditAppointmentReminderJobEntryDialog from '../components/EditAppointmentReminderJobEntryDialog';

const AppointmentReminderJobEntries = () => {
  const { t } = useLanguage();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sendStatus: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);
  const [jobInfo, setJobInfo] = useState(null);
  const [errorCount, setErrorCount] = useState(0);
  const [templateDialog, setTemplateDialog] = useState({
    isOpen: false,
    clid: null,
    templateName: null
  });
  const [editDialog, setEditDialog] = useState({
    isOpen: false,
    entry: null
  });

  // Send status options for filter
  const sendStatusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'sent', label: 'Sent' },
    { value: 'failed', label: 'Failed' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'rescheduled', label: 'Rescheduled' }
  ];

  // Fetch job entries data
  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);

      const skip = (currentPage - 1) * itemsPerPage;
      const response = await appointmentReminderJobService.getAppointmentReminderJobEntries(
        jobId,
        skip,
        itemsPerPage,
        searchTerm || null,
        filters.sendStatus || null
      );

      setEntries(response.items || []);
      setTotal(response.total || 0);

      // Count entries with error messages
      const entriesWithErrors = (response.items || []).filter(entry => entry.error_message);
      setErrorCount(entriesWithErrors.length);
    } catch (err) {
      console.error('Error fetching appointment reminder job entries:', err);
      setError('Failed to load appointment reminder job entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when dependencies change
  useEffect(() => {
    if (jobId) {
      fetchEntries();
    }
  }, [jobId, currentPage, searchTerm, filters]);

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      sendStatus: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmed':
        return 'bg-emerald-100 text-emerald-800';
      case 'rescheduled':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'failed':
        return <XCircleIcon className="w-4 h-4" />;
      case 'pending':
        return <ClockIcon className="w-4 h-4" />;
      case 'confirmed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'rescheduled':
        return <ArrowPathIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/appointment-reminder-jobs');
  };

  // Handle template name click
  const handleTemplateClick = (clid, templateName) => {
    if (templateName && templateName !== 'N/A') {
      setTemplateDialog({
        isOpen: true,
        clid: clid,
        templateName: templateName
      });
    }
  };

  // Handle template dialog close
  const handleTemplateDialogClose = () => {
    setTemplateDialog({
      isOpen: false,
      clid: null,
      templateName: null
    });
  };

  // Handle edit entry
  const handleEditEntry = (entry) => {
    setEditDialog({
      isOpen: true,
      entry: entry
    });
  };

  // Handle edit dialog close
  const handleEditDialogClose = () => {
    setEditDialog({
      isOpen: false,
      entry: null
    });
  };

  // Handle edit dialog save
  const handleEditDialogSave = (updatedEntry) => {
    // Update the entry in the local state
    setEntries(prevEntries =>
      prevEntries.map(entry =>
        entry.id === updatedEntry.id ? { ...entry, ...updatedEntry } : entry
      )
    );

    // Refresh the data to ensure consistency
    fetchEntries();
  };

  // Handle delete entry
  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(`Are you sure you want to delete this entry for ${entry.patient_name || 'this patient'}?`)) {
      return;
    }

    try {
      setLoading(true);
      await appointmentReminderJobService.deleteAppointmentReminderJobEntry(entry.id);

      // Remove the entry from local state
      setEntries(prevEntries => prevEntries.filter(e => e.id !== entry.id));
      setTotal(prevTotal => prevTotal - 1);

      // Show success message
      toast.success('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete entry. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Job Entries
                </h1>
                <p className="text-gray-600 mt-1">
                  Appointment reminder entries for job {jobId.slice(-8)}
                </p>
                {errorCount > 0 && (
                  <div className="mt-2 flex items-center">
                    <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                    <span className="text-sm text-red-600 font-medium">
                      {errorCount} {errorCount === 1 ? 'entry has' : 'entries have'} error messages
                    </span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={fetchEntries}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshIcon className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <SearchBar
                placeholder="Search by patient name, phone, or doctor..."
                onSearch={handleSearch}
                className="w-full"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <FilterIcon className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {/* Send Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send Status
                  </label>
                  <select
                    value={filters.sendStatus}
                    onChange={(e) => handleFilterChange('sendStatus', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {sendStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Entries Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading entries...</span>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-gray-900">No entries found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filters.sendStatus
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No appointment reminder entries have been created for this job yet.'
                }
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Patient
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Doctor
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clinic
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appointment
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        References
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Template
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remarks
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      {/* <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Error
                      </th> */}
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sent At
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {entries.map((entry) => (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                          {entry.id ? entry.id.slice(-8) : 'N/A'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {entry.patient_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <div>
                              {entry.district_code && (
                                <span className="text-xs text-gray-500 mr-1">
                                  ({entry.district_code})
                                </span>
                              )}
                              <span>
                                {entry.phone_number || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.doctor_name || 'N/A'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="font-medium">
                              {entry.clinic_name || 'N/A'}
                            </div>
                            {entry.clinic_address && (
                              <div className="flex items-center mt-1">
                                <span className="text-xs text-gray-500 truncate max-w-xs">
                                  {entry.clinic_address}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="font-medium">
                              {entry.appointment_date || 'N/A'}
                            </div>
                            <div className="text-gray-500">
                              {entry.appointment_start_time || 'N/A'}
                              {entry.appointment_duration && (
                                <span className="ml-2">
                                  ({entry.appointment_duration}min)
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-500 space-y-1">
                            {entry.reference_doctor_id && (
                              <div>Dr: {entry.reference_doctor_id.slice(-8)}</div>
                            )}
                            {entry.reference_clinic_id && (
                              <div>Clinic: {entry.reference_clinic_id.slice(-8)}</div>
                            )}
                            {entry.reference_appointment_id && (
                              <div>Appt: {entry.reference_appointment_id.slice(-8)}</div>
                            )}
                            {entry.reference_patient_id && (
                              <div>Patient: {entry.reference_patient_id.slice(-8)}</div>
                            )}
                            {!entry.reference_doctor_id && !entry.reference_clinic_id && !entry.reference_appointment_id && (
                              <div>No references</div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {entry.template_name && entry.template_name !== 'N/A' ? (
                            <button
                              onClick={() => handleTemplateClick(entry.clid, entry.template_name)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {entry.template_name}
                            </button>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 max-w-xs truncate" title={entry.remarks || 'No remarks'}>
                            {entry.remarks || 'N/A'}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(entry.send_status)}`}>
                            {getStatusIcon(entry.send_status)}
                            <span className="ml-1 capitalize">{entry.send_status || 'Unknown'}</span>
                          </span>
                        </td>
                        {/* <td className="px-3 py-4 whitespace-nowrap"> */}
                          {/* {entry.error_message ? (
                            <div className="text-xs text-red-600 truncate max-w-xs" title={entry.error_message}>
                              {entry.error_message}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No errors</span>
                          )}
                        </td> */}
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {entry.sent_at ? formatDate(entry.sent_at) : 'N/A'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(entry.created_at)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(entry.updated_at)}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm text-gray-500 sticky right-0 bg-white z-10">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEditEntry(entry)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg cursor-pointer transition-colors duration-200"
                              title="Edit entry"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEntry(entry)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg cursor-pointer transition-colors duration-200"
                              title="Delete entry"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > itemsPerPage && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(total / itemsPerPage)}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Summary */}
        {!loading && entries.length > 0 && (
          <div className="mt-6 text-sm text-gray-500 text-center">
            <div>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} entries
            </div>
            {errorCount > 0 && (
              <div className="mt-1 flex items-center justify-center">
                <XCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                <span className="text-red-600 font-medium">
                  {errorCount} {errorCount === 1 ? 'entry has' : 'entries have'} errors
                </span>
              </div>
            )}
          </div>
        )}
        {/* WABA Template Preview Dialog */}
        <WABATemplatePreviewDialog
          isOpen={templateDialog.isOpen}
          onClose={handleTemplateDialogClose}
          clid={templateDialog.clid}
          templateName={templateDialog.templateName}
        />

        {/* Edit Dialog */}
        <EditAppointmentReminderJobEntryDialog
          isOpen={editDialog.isOpen}
          onClose={handleEditDialogClose}
          entry={editDialog.entry}
          onSave={handleEditDialogSave}
        />
      </div>
    </div>
  );
};

export default AppointmentReminderJobEntries;
